import { Link } from "react-router-dom";
import {
  Stamp,
  FileSignature,
  Home,
  Globe,
  Car,
  Monitor,
  ShieldCheck,
  Clock,
  Check,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const RON_URL = process.env.REACT_APP_RON_URL;

const services = [
  {
    icon: FileSignature,
    title: "General Notarization",
    description: "Acknowledgments, jurats, and oaths for everyday documents.",
    details: [
      "Acknowledgments & jurats",
      "Affidavits and sworn statements",
      "Power of attorney documents",
      "Copy certifications where permitted",
      "Oaths and affirmations"
    ],
    note: "Please bring a valid, unexpired government-issued photo ID."
  },
  {
    icon: Home,
    title: "Loan & Real Estate Signings",
    description: "Accurate, on-time signings for mortgages, refinances, and closings.",
    details: [
      "Purchase and refinance packages",
      "HELOC and reverse mortgage signings",
      "Seller and buyer document sets",
      "Coordination with title & escrow",
      "Timely package return"
    ],
    note: "Available for individuals, lenders, title companies, and signing services."
  },
  {
    icon: Car,
    title: "Mobile Notary",
    description: "We come to you — home, office, hospital, or a convenient meeting spot.",
    details: [
      "Flexible on-site appointments",
      "Evening and weekend availability",
      "Hospital and care facility visits",
      "Business and group signings",
      "Serving the NYC metro area"
    ],
    note: "Travel availability and fees vary by location."
  },
  {
    icon: Globe,
    title: "Apostille & Authentication Help",
    description: "Guidance to get documents ready for international use.",
    details: [
      "Apostille process guidance",
      "Document preparation review",
      "State authentication coordination",
      "Notarization for foreign use",
      "Referrals where needed"
    ],
    note: "We assist with preparation; final apostilles are issued by the state."
  }
];

const benefits = [
  {
    icon: ShieldCheck,
    title: "Commissioned & Reliable",
    description: "Properly commissioned notary services you can trust for important documents."
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    description: "Evening, weekend, and mobile appointments to fit your schedule."
  },
  {
    icon: Stamp,
    title: "Done Right",
    description: "Careful attention to detail so your documents are accepted the first time."
  }
];

export default function Notary() {
  return (
    <div data-testid="notary-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Notary Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Trusted Notary Services, When You Need Them
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              From everyday notarizations to loan signings and mobile appointments, we make certifying your important documents simple, fast, and reliable.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
                data-testid="notary-cta-btn"
              >
                <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                  Schedule an Appointment
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </Button>
              {RON_URL && (
                <Button
                  asChild
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-6 text-lg font-medium bg-transparent"
                  data-testid="notary-ron-btn"
                >
                  <a href={RON_URL} target="_blank" rel="noopener noreferrer">
                    Online Digital Notary (RON)
                    <Monitor className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="flex items-center gap-4" data-testid={`notary-benefit-${index}`}>
                <div className="w-14 h-14 rounded-full bg-bone flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-7 h-7 text-bronze" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-navy">{benefit.title}</h3>
                  <p className="font-body text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              Notary Support Services
            </h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">
              Professional notarization for individuals, families, and businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={service.title}
                className="bg-white border-0 shadow-lg"
                data-testid={`notary-service-${index}`}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-7 h-7 text-bronze" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-xl text-navy mb-2">
                        {service.title}
                      </CardTitle>
                      <p className="font-body text-gray-600">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  {service.note && (
                    <p className="font-body text-xs text-gray-500 italic pt-4 border-t border-gray-100">
                      {service.note}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Block */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">How It Works</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-6">
                Simple, Hassle-Free Notarization
              </h2>
              <p className="font-body text-gray-600 mb-8">
                Whether you need a single signature notarized or a full loan package handled, we make the process quick and stress-free.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Book Your Appointment</h4>
                    <p className="font-body text-gray-600 text-sm">Choose a time and location that works for you.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Bring Your Documents & ID</h4>
                    <p className="font-body text-gray-600 text-sm">Have your unsigned documents and valid photo ID ready.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Sign & Get Notarized</h4>
                    <p className="font-body text-gray-600 text-sm">We witness your signature and complete the notarization.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-bone rounded-2xl p-8 lg:p-12">
              <h3 className="font-heading text-2xl font-bold text-navy mb-6">
                Need a Document Notarized?
              </h3>
              <p className="font-body text-gray-600 mb-8">
                Reach out to schedule an in-person or mobile notary appointment. We're here to help you get it done right.
              </p>
              <div className="space-y-4">
                <Button
                  asChild
                  className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium"
                  data-testid="notary-book-btn"
                >
                  <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                    Schedule an Appointment
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-navy text-navy hover:bg-navy hover:text-white rounded-full py-6 font-medium"
                  data-testid="notary-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Questions About Notary Services?
          </h2>
          <p className="font-body text-gray-300 mb-8 max-w-2xl mx-auto">
            Not sure what you need? Get in touch and we'll point you in the right direction.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="notary-cta-final"
          >
            <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
              Schedule a Call
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
